import * as React from "react";
import { observer, inject } from "mobx-react";

import { Utils } from "../../common/utils";
import { CommentStore, Comment } from "Stores/dashboardStore/comments";
import {AppStore, AttachmentStore, CommonStore, HistoryStore, SnapshotStore} from "Stores/index";

import { Button, Container, Row, Col, Spinner } from "react-bootstrap";

import { TicketCommentForm } from "./CommentForm";
import { AttachmentFile, AttachmentSelector } from "Components/Common/AttachmentSelector";
import { Attachment } from "Stores/attachmentStore";
import { AttachmentLink } from "Components/Common/AttachmentLink";
import { SimpleCheckbox } from "Components/Common/SimpleCheckbox";
import {Link} from "react-router-dom";
import EyeIcon from "Components/Icons/EyeIcon";
import {DashboardView} from "Stores/commonStore";
import TooltipButton from "Components/Common/TooltipButton";
import TipTapEditor, { TipTapEditorHandle } from "Components/Common/TheEditor";
import { draftToProseMirrorJSON, safeJsonParse } from "@/lib/convertDraftToProseMirror";

interface CommentListProps {
    appStore?: AppStore
    commentStore?: CommentStore
    commonStore?: CommonStore
    attachmentStore?: AttachmentStore
    snapshotStore?: SnapshotStore
    historyStore?: HistoryStore,
    dashboardId?: number | null
    dashboardViews?: DashboardView[]
    ticketId: string
    refreshView?: string | null
    canAdd: boolean
    onDelete: (commentId: string) => void
    onAttachmentDelete: (attachmentId: string, commentId: string) => void
    isOnDashboard: boolean
    isOnResource: boolean
}

interface CommentListState {
    showFormForId: string | undefined
    formatted_comment: string
    comment: string
    includeSnapshot: boolean
    attachments: AttachmentFile[]
    refreshView?: string | null
    isSaving: boolean
}

interface IEditComment {
    formatted_comment: string
    comment: string
}

@inject("commentStore", "commonStore", "attachmentStore", "appStore", "snapshotStore", "historyStore")
@observer
export class TicketCommentList extends React.Component<CommentListProps, CommentListState> {

    constructor(props) {
        super(props);
        this.state = {
            showFormForId: undefined,
            formatted_comment: "",
            comment: "",
            attachments: [],
            isSaving: false,
            includeSnapshot: false,
            refreshView: this.props.refreshView
        };
    }

    dropRef = undefined;
    resetNewCommentEditor;

    updateStores = (callback?: () => void) => {
        if(this.props.commentStore){
            Promise.all([
                this.props.commentStore.fetchCommentsForTicket(this.props.ticketId),
                this.props.attachmentStore!.getAllAttachmentsForActionplan(this.props.ticketId, true),
                this.props.historyStore!.getAllHistoryForActionPlan(this.props.ticketId),
                this.props.commonStore!.fetchDashboardViews(this.props.dashboardId, this.props.ticketId)
        ]).then(() => {
                if(callback){
                    callback();
                }
            });
        }
    };

    handleCancel = () => {
        this.setState({showFormForId: undefined});
    };

    deleteComment = (comment: Comment) => {
        if(!this.props.commentStore || !this.props.appStore || !this.props.attachmentStore) return;
        this.props.appStore.showConfirmModal({
            element: <span>Are you sure you want to delete this comment?</span>,
            accept: () => {
                this.props.onDelete(comment.id);
            },
            cancel: this.handleCancel,
            showActions: true,
        });
    };

    editorRef = React.createRef<TipTapEditorHandle>();

    handleSaveComment = async () => {
        const currentUser = Utils.getCurrentUser();
        if(!this.props.commentStore || !this.props.ticketId || !currentUser || !this.props.attachmentStore) return;

        this.setState({isSaving: true}, async () => {

            const attachmentsToUpload: AttachmentFile[] = this.state.attachments;

            const data: any = {
                formatted_comment: this.state.formatted_comment,
                comment: this.state.comment,
                created_by: currentUser.email,
            };

            let hasSnapshotAttachment = false;
            if(this.state.includeSnapshot && this.props.isOnDashboard){
                const { url, attachmentFile } = await Utils.getSnapshotAttachmentFileAndUrl(this.props.snapshotStore!);

                if(attachmentFile !== undefined){
                    attachmentsToUpload.push(attachmentFile);
                    hasSnapshotAttachment = true;
                }

                Object.assign(data, {
                    dashboard_image_url: url,
                });
            }

            if(attachmentsToUpload.length > 0){
                Object.assign(data, {
                    attachments: await this.props.attachmentStore!.createAndUploadAttachments(attachmentsToUpload),
                });
            }

            // If the snapshot has already been added to the list of attachments, we want to skip generating the snapshot in the api.
            this.props.commentStore!.addCommentToTicket(this.props.ticketId, data, hasSnapshotAttachment).then(() => {
                this.editorRef.current?.clear();

                this.updateStores(() => {
                    this.setState({
                        formatted_comment: "",
                        comment: "",
                        attachments: [],
                        isSaving: false,
                    });
                });
            });
        });
    };

    editComment = ({ formatted_comment, comment }: IEditComment) => {
        if(!this.props.commentStore || !this.state.showFormForId) return;
        const commentId = this.state.showFormForId;
        if(!commentId) return;
        this.setState({isSaving: true}, () => {
            this.props.commentStore!.updateComment(this.state.showFormForId!, formatted_comment, comment, this.props.ticketId).then(() => {
                this.updateStores(() => {
                    this.setState({showFormForId: undefined, isSaving: false});
                });
            });
        });
    };

    showEditComment = (comment: Comment) => {
        this.setState({showFormForId: comment.id});
    };

    isCommentValid = () => {
        return this.state.formatted_comment !== "";
    };

    handleAttachmentsChange = (attachments: AttachmentFile[]) => {
        this.setState({attachments});
    };

    handleIncludeSnapshotChange = (next) => {
        this.setState({includeSnapshot: next});
    };

    handleDeleteAttachment = (att: Attachment, commentId: string) => {
        this.props.appStore!.showConfirmModal({
            element: (
                <span>{`Are you sure you want to delete ${att.file_name}?`}</span>
            ),
            accept: () => {
                this.props.onAttachmentDelete(att.id, commentId);
            },
            cancel: () => {},
            showActions: true
        });
    };

    handleEditorChange = (editorContent) => {
        this.setState({formatted_comment: editorContent});
    };

    handleEditorReset = (commentEditorResetFunction: () => void) => {
        this.resetNewCommentEditor = commentEditorResetFunction;
    };

    getSnapshotLink = (viewType: string, commentId: string) => {
        if (!this.props.dashboardId || !this.props.dashboardViews) {
            return;
        }
        const view = this.props.dashboardViews.find(view => view.view_type === viewType && view.comment_id == commentId);
        if (!view) {
            return;
        }
        return (
            <Link
                className="btn btn-icon"
                style={{display: "inline-flex", alignItems: "flex-end"}}
                onClick={() => {
                    const nextRefreshView = this.state.refreshView ? Number(this.state.refreshView) + 1 : 0;
                    this.setState({refreshView: nextRefreshView.toString()}, () => {
                        window.scrollTo({ top: 0, behavior: "smooth" });
                    });
                }}
                to={Utils.getDashboardViewUrl(this.props.dashboardId, view.ruuid, this.state.refreshView ? Number(this.state.refreshView) + 1: 0, this.props.ticketId)}>
                <EyeIcon fill="#c96bc6" />
            </Link>
        );
    };

    getSnapshotCopyButton = (viewType: string, commentId: string) => {
        if (!this.props.dashboardId || !this.props.dashboardViews) {
            return;
        }
        const view = this.props.dashboardViews.find(view => view.view_type === viewType && view.comment_id === commentId);
        if (!view) {
            return;
        }
        const nextRefreshView = this.state.refreshView ? Number(this.state.refreshView) + 1 :0;
        const copyUrl = `${window.location.origin}${Utils.getDashboardViewUrl(this.props.dashboardId, view.ruuid, nextRefreshView, this.props.ticketId)}`;
        return (
            <TooltipButton onClick={() => navigator.clipboard.writeText(copyUrl)} />
        );
    };

    handleRef = ref => this.dropRef = ref;

    renderAttachments = (c: Comment) =>
        (c.attachments.length > 0
            ? <Row className="attachments">
                <Col md={3}/>
                <Col md={8}>{c.attachments.map((att: Attachment) => this.state.showFormForId === c.id
                    ?(<AttachmentLink key={att.id} attachment={att} includeIcon={true} associatedObjectId={c.id} onDelete={this.handleDeleteAttachment}/>)
                    :(<AttachmentLink key={att.id} attachment={att} includeIcon={true}/>)
                )}
                    {this.getSnapshotLink("comment", c.id)}
                    {this.getSnapshotCopyButton("comment", c.id)}
                </Col>
            </Row>
            : null);

    renderComments = () =>
    (this.props.commentStore!.comments!.map((c: Comment) => {
        const parsedComment = safeJsonParse(c.formatted_comment);
        const commentContent = parsedComment && parsedComment.blocks ? draftToProseMirrorJSON(parsedComment as any) : parsedComment;
        return (
            <div className="comment-container" key={c.id}>
                <Row className="comment">
                    <Col className="text-center d-flex align-items-center justify-content-center" md={ 2 }>{ c.created_by }</Col>
                    <Col className="text-center d-flex align-items-center justify-content-center" md={ 1 }>{ Utils.getValidDateFromString(c.created, "MM/DD/YY") }</Col>
                    { this.state.showFormForId === c.id
                        ? <Col className="d-flex align-items-center justify-content-center" md={ 8 }>
                            <TicketCommentForm
                                currentComment={ c }
                                handleCancel={ this.handleCancel }
                                handleSave={ this.editComment }
                                isProcessing={ this.props.commentStore!.isProcessing || this.state.isSaving}/></Col>
                        : <Col className="text-left d-flex align-items-center justify-content-center" md={ 8 }>
                            <TipTapEditor id={`comment-${c.id}`} value={commentContent} editable={false} showToolbar={false} />
                        </Col> }
                    <Col className="text-center d-flex align-items-center justify-content-center actions" md={ 1 }>
                        <Button
                            disabled={ !c.permissions.can_edit_comment }
                            onClick={ () => this.showEditComment(c) }
                            style={{paddingTop: "2px"}}
                        >
                            <i className="icon icon-pencil-square-o"/>
                        </Button>
                        <Button disabled={ !c.permissions.can_delete_comment }
                                onClick={ () => this.deleteComment(c) }>
                            <i className="icon icon-times"/>
                        </Button>
                    </Col>
                </Row>
                { this.renderAttachments(c) }
            </div>
        );}));

    render(){
        if(!this.props.commentStore) return null;
        return (
            <div className="comments" ref={this.handleRef}>
            { this.props.commentStore.comments && this.props.commentStore.comments.length > 0
                ?<Col className="comment-list">
                    <Row className="header">
                        <Col className="text-center" md={2}>Commenter</Col>
                        <Col className="text-center" md={1}>Date</Col>
                        <Col className="text-center" md={8}>Comment</Col>
                        <Col md={1}/>
                    </Row>
                    {this.renderComments()}
                </Col>: null }
                <h5>Add a comment:</h5>
                <Container fluid>
                    <Row className="new-comment">
                        <Col xs={11} className="textarea-container px-0 d-flex align-items-center justify-content-center">
                            <TipTapEditor
                                id="new-comment"
                                ref={this.editorRef}
                                value={this.state.formatted_comment}
                                onChange={this.handleEditorChange}
                                onGetPlainText={(content) => this.setState({ comment: content })}
                            />
                        </Col>
                        <Col xs={1} className="justify-content-center align-items-center button-container">
                            <Button
                                disabled={this.props.commentStore.isProcessing || this.state.isSaving || !this.isCommentValid()}
                                onClick={this.handleSaveComment}
                                variant="secondary">
                                {this.props.commentStore.isProcessing || this.state.isSaving
                                    ? <Spinner animation="border"/>
                                    :"Add"}
                            </Button>
                        </Col>
                    </Row>
                </Container>
                {this.props.isOnDashboard && !this.props.isOnResource
                    ? <Row className="comment-snapshot align-items-center">
                        <SimpleCheckbox
                            isChecked={this.state.includeSnapshot}
                            id="comment-include-snapshot"
                            onChange={this.handleIncludeSnapshotChange}
                            label="Include Snapshot of Current Dashboard"/>
                    </Row>
                    : null}
                <Row>
                    <AttachmentSelector attachments={this.state.attachments} onChange={this.handleAttachmentsChange} dropRef={this.dropRef}/>
                </Row>
            </div>
        );
    }
}
